/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTheme } from '../../hooks/useTheme';
import { Wizard, WizardStep, Card, Input, Tile, Switch, TextArea } from '@dsi/react-eds';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';
import { useLoader } from '../../hooks/useLoader';
import { AxiosError } from 'axios';
import { Enm, SoftwareRepository, EiapCluster, AutomationFlow, SelectiveOnboardingConfig } from '../../types/automation-flow.types';
import { useRepositories, useEiapClusters, useEnms, useProductLines } from '../../hooks/useAutomationData';
import { automationFlowService } from '../../services/automationFlowService';
import { getRepositoryTypeInfo, getStatusColor, RepositoryType } from '../../utils/repositoryTypes';

/** Default when loading a flow that has no selectiveOnboarding config. */
const DEFAULT_SELECTIVE_ONBOARDING: SelectiveOnboardingConfig = { enabled: false };

function normalizeSelectiveOnboarding(raw: unknown): SelectiveOnboardingConfig {
  if (!raw || typeof raw !== 'object') return DEFAULT_SELECTIVE_ONBOARDING;
  const o = raw as Record<string, unknown>;
  return { enabled: typeof o.enabled === 'boolean' ? o.enabled : false };
}

function getStageModel(scope: string): string {
  switch (scope) {
    case 'full':
      return 'Full_Automation';
    case 'onboarding_deployment':
      return 'Onboarding_Deployment';
    case 'onboarding_only':
      return 'Onboarding_Only';
    default:
      return 'Onboarding_Only';
  }
}

function deriveScope(stageModel?: string): string {
  switch (stageModel) {
    case 'Full_Automation':
      return 'full';
    case 'Onboarding_Deployment':
      return 'onboarding_deployment';
    case 'Onboarding_Only':
      return 'onboarding_only';
    default:
      return 'onboarding_only';
  }
}

export default function RAppsAutomationWizard() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { addNotifications } = useSwobNotifications();
  const { showLoader, hideLoader } = useLoader();

  const templateState = location.state as {
    selectedCard?: { title?: string; wizardTitle?: string };
    preSelectedProductType?: string;
    flowId?: string;
    mode?: 'edit' | 'create';
  } | null;

  const { repositories, fetchRepositories } = useRepositories();
  const { eiapClusters, fetchEiapClusters } = useEiapClusters();
  const { enms, fetchEnms } = useEnms();
  const { productLines, fetchProductLines } = useProductLines();

  const [originalFlowName, setOriginalFlowName] = useState('');

  const [formData, setFormData] = useState<{
    flowName: string;
    flowDescription: string;
    environmentType: 'verified' | 'unverified' | '';
    scope: 'onboarding_only' | 'onboarding_deployment' | 'full' | '';
    repository: string;
    productLine: string;
    eiapCluster: string;
    enms: string[];
    selectiveOnboarding: SelectiveOnboardingConfig;
    deploymentAutomation: boolean;
    testAutomation: boolean;
  }>({
    flowName: '',
    flowDescription: '',
    environmentType: 'unverified',
    scope: 'full',
    repository: '',
    productLine: '',
    eiapCluster: '',
    enms: [],
    selectiveOnboarding: { enabled: true },
    deploymentAutomation: true,
    testAutomation: false
  });

  const isVerified = formData.environmentType === 'verified';
  const showDeploymentStep = templateState?.mode !== 'edit' && (formData.scope === 'onboarding_deployment' || formData.scope === 'full');
  const showTestingStep = templateState?.mode !== 'edit' && formData.scope === 'full';

  const validateFlowData = (
    flow: AutomationFlow,
    fetchedRepos: SoftwareRepository[],
    fetchedClusters: EiapCluster[],
    fetchedEnms: Enm[]
  ) => {
    const repoId = flow.configuration?.repository;
    const repoExists = repoId === 'swgw' || repoId === 'internal-oci' || fetchedRepos.some((r) => r.id === repoId);
    if (!repoExists && repoId) {
      addNotifications([
        {
          title: 'Warning - Software Repository',
          icon: 'icon-triangle-warning',
          description: 'The previously configured software repository is no longer available.',
          color: 'yellow',
          timestamp: new Date()
        }
      ]);
    }
    const eiapClusterId = flow.eiapClusterId || '';
    const eiapExists = !eiapClusterId || fetchedClusters.some((c) => c.id === eiapClusterId);
    if (!eiapExists) {
      addNotifications([
        {
          title: 'Warning - EIAP Cluster',
          icon: 'icon-triangle-warning',
          description: 'The previously configured EIAP Cluster is no longer available.',
          color: 'yellow',
          timestamp: new Date()
        }
      ]);
    }
    const savedEnmIds = flow.enms?.map((e: Enm) => e.id) || [];
    const validEnmIds = savedEnmIds.filter((id: string) => fetchedEnms.some((e) => e.id === id));
    if (validEnmIds.length < savedEnmIds.length && savedEnmIds.length > 0) {
      addNotifications([
        {
          title: 'Warning - ENMs',
          icon: 'icon-triangle-warning',
          description: 'One or more previously configured ENMs are no longer available.',
          color: 'yellow',
          timestamp: new Date()
        }
      ]);
    }
    return { repository: repoExists ? repoId : '', eiapCluster: eiapExists ? eiapClusterId : '', enms: validEnmIds };
  };

  const loadAutomationFlow = async (
    flowId: string,
    fetchedRepos: SoftwareRepository[],
    fetchedClusters: EiapCluster[],
    fetchedEnms: Enm[]
  ) => {
    try {
      const resp = await automationFlowService.getById(flowId);
      const flow = resp.data;
      const validated = validateFlowData(flow, fetchedRepos, fetchedClusters, fetchedEnms);
      const envType = flow.configuration?.environmentType || (flow.artifactTypeRule === 'verified' ? 'verified' : 'unverified');
      const scope = flow.configuration?.scope || deriveScope(flow.stageModel || flow.template?.name);
      setFormData({
        flowName: flow.name || '',
        flowDescription: flow.description || '',
        environmentType: envType,
        scope,
        repository: validated.repository || '',
        productLine: flow.productLineId || '',
        eiapCluster: validated.eiapCluster || '',
        enms: validated.enms,
        selectiveOnboarding: normalizeSelectiveOnboarding(flow.configuration?.selectiveOnboarding),
        deploymentAutomation: flow.configuration?.deploymentAutomation ?? false,
        testAutomation: flow.configuration?.testAutomation ?? false
      });
      setOriginalFlowName(flow.name || '');
    } catch (error) {
      addNotifications([
        {
          title: 'Error - Automation Flow',
          icon: 'icon-triangle-warning',
          description:
            error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while fetching automation flow.',
          color: 'red',
          timestamp: new Date()
        }
      ]);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      showLoader();
      try {
        const [reposResult, clustersResult, enmsResult] = await Promise.all([
          fetchRepositories().catch(() => []),
          fetchEiapClusters().catch(() => []),
          fetchEnms().catch(() => [])
        ]);
        await fetchProductLines('rApps').catch(() => []);

        if (!reposResult || reposResult.length === 0) {
          setFormData((prev) => ({ ...prev, repository: 'swgw' }));
        }
        if (clustersResult && clustersResult.length === 1) {
          setFormData((prev) => ({ ...prev, eiapCluster: clustersResult[0].id, enms: [] }));
        }

        if (templateState?.mode === 'edit' && templateState?.flowId) {
          await loadAutomationFlow(templateState.flowId, reposResult || [], clustersResult || [], enmsResult || []);
        }
      } finally {
        setTimeout(() => hideLoader(), 500);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedEiapCluster = eiapClusters.find((c) => c.id === formData.eiapCluster);
  const availableEnms = selectedEiapCluster ? enms.filter((enm) => enm.linkedEiapClusters.includes(selectedEiapCluster.id)) : enms;

  useEffect(() => {
    if (availableEnms.length === 1 && formData.enms.length === 0) {
      setFormData((prev) => ({ ...prev, enms: [availableEnms[0].id] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableEnms.length]);

  const handleCancel = () => navigate('/automation-flows/new');

  const handleFinish = async () => {
    if (!formData.flowName.trim()) {
      addNotifications([
        {
          title: 'Validation Error',
          icon: 'icon-triangle-warning',
          description: 'Automation Flow Name is required.',
          color: 'red',
          timestamp: new Date()
        }
      ]);
      return;
    }
    if (!formData.repository) {
      addNotifications([
        {
          title: 'Validation Error',
          icon: 'icon-triangle-warning',
          description: 'Please select a Software Repository.',
          color: 'red',
          timestamp: new Date()
        }
      ]);
      return;
    }
    if (!formData.eiapCluster) {
      addNotifications([
        {
          title: 'Validation Error',
          icon: 'icon-triangle-warning',
          description: 'Please select an EIAP Cluster.',
          color: 'red',
          timestamp: new Date()
        }
      ]);
      return;
    }
    if (formData.enms.length === 0) {
      addNotifications([
        {
          title: 'Validation Error',
          icon: 'icon-triangle-warning',
          description: 'Please select at least one ENM.',
          color: 'red',
          timestamp: new Date()
        }
      ]);
      return;
    }

    showLoader();
    try {
      const configuration: Record<string, unknown> = {
        repository: formData.repository,
        repositoryType: formData.repository === 'swgw' ? 'swgw' : formData.repository === 'internal-oci' ? 'internal' : 'external',
        environmentType: formData.environmentType,
        scope: formData.scope,
        selectiveOnboarding: formData.selectiveOnboarding
      };
      if (showDeploymentStep) configuration.deploymentAutomation = formData.deploymentAutomation;
      if (showTestingStep) configuration.testAutomation = formData.testAutomation;

      if (templateState?.mode === 'edit' && templateState?.flowId) {
        await automationFlowService.update(templateState.flowId, {
          name: formData.flowName.trim(),
          description: formData.flowDescription.trim(),
          eiapClusterId: formData.eiapCluster || null,
          enmIds: formData.enms,
          configuration
        });
        addNotifications([
          {
            title: 'Success',
            icon: 'icon-check',
            description: 'rApps Automation Flow updated successfully!',
            color: 'green',
            timestamp: new Date()
          }
        ]);
      } else {
        await automationFlowService.create({
          name: formData.flowName.trim(),
          description: formData.flowDescription.trim(),
          cardName: 'rApps Workflow',
          productType: 'rApps',
          stageModel: getStageModel(formData.scope),
          artifactTypeRule: formData.environmentType === 'verified' ? 'verified' : 'unverified',
          eiapClusterId: formData.eiapCluster || null,
          enmIds: formData.enms,
          configuration,
          status: 'active'
        });
        addNotifications([
          {
            title: 'Success',
            icon: 'icon-check',
            description: 'rApps Automation Flow created successfully!',
            color: 'green',
            timestamp: new Date()
          }
        ]);
      }
      navigate('/automation-flows');
    } catch (error) {
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description: error instanceof AxiosError ? error.response?.data?.message || error.message : 'Failed to save automation flow.',
          color: 'red',
          timestamp: new Date()
        }
      ]);
    } finally {
      setTimeout(() => hideLoader(), 500);
    }
  };

  const baseTitle = templateState?.selectedCard?.wizardTitle || templateState?.selectedCard?.title || 'rApps Workflow';
  const wizardTitle = templateState?.mode === 'edit' && originalFlowName ? `${baseTitle}: ${originalFlowName}` : baseTitle;

  return (
    <div className={`${theme ? 'light-theme' : 'dark-theme'}`}>
      <ReactEDSWrapper component={Tile} title={wizardTitle} id="configure-rapps-automation-flow-tile">
        <p style={{ marginBottom: '16px', color: '#8b8b8b' }}>Follow the steps to configure a new automation workflow.</p>
        <ReactEDSWrapper
          component={Wizard}
          onCancel={handleCancel}
          onFinish={handleFinish}
          nextBtnLabel="Next"
          previousBtnLabel="Previous"
          cancelBtnLabel="Cancel"
          finishBtnLabel={templateState?.mode === 'edit' ? 'Update Automation Flow' : 'Create Automation Flow'}
        >
          {/* Step 1: Initial Information */}
          <ReactEDSWrapper
            component={WizardStep}
            stepTitle="Initial Information"
            valid={!!formData.flowName && !!formData.environmentType && !!formData.scope}
          >
            <p style={{ color: '#8b8b8b', marginBottom: '16px' }}>Provide the initial details to start setting up your workflow.</p>
            <div className="row">
              <div className="column sm-12">
                <ReactEDSWrapper
                  component={Input}
                  required
                  label="Name"
                  placeholder="Automation Flow 1"
                  value={formData.flowName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, flowName: e.target.value })}
                />
              </div>
              <div className="column sm-12" style={{ marginTop: '16px' }}>
                <ReactEDSWrapper
                  component={TextArea}
                  label="Description"
                  placeholder="Automation Flow Description"
                  value={formData.flowDescription}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, flowDescription: e.target.value })}
                  maxLength={150}
                  style={{ width: '25%' }}
                />
              </div>
            </div>

            {/* Artifact Readiness */}
            <div style={{ marginTop: '24px' }}>
              <h4 style={{ marginBottom: '4px' }}>Artifact Readiness</h4>
              <p style={{ color: '#8b8b8b', marginBottom: '12px' }}>Select the current readiness state of your artifact.</p>
              <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
                <div style={{ marginBottom: '8px' }}>
                  <input
                    id="env-unverified"
                    name="environmentType"
                    type="radio"
                    checked={formData.environmentType === 'unverified'}
                    onChange={() => setFormData({ ...formData, environmentType: 'unverified', repository: '' })}
                  />
                  <label htmlFor="env-unverified">Requires Validation</label>
                  <p style={{ color: '#8b8b8b', fontSize: '12px', marginLeft: '24px', marginTop: '2px' }}>
                    New artifact that has not yet been validated through deployment and testing.
                  </p>
                </div>
                <div>
                  <input
                    id="env-verified"
                    name="environmentType"
                    type="radio"
                    checked={formData.environmentType === 'verified'}
                    onChange={() =>
                      setFormData({
                        ...formData,
                        environmentType: 'verified',
                        repository: 'internal-oci',
                        ...(formData.scope === 'full' && { scope: 'onboarding_deployment' })
                      })
                    }
                  />
                  <label htmlFor="env-verified">Ready for Release</label>
                  <p style={{ color: '#8b8b8b', fontSize: '12px', marginLeft: '24px', marginTop: '2px' }}>
                    Artifact has already completed validation through deployment and testing.
                  </p>
                </div>
              </fieldset>
            </div>

            {/* Automation Scope */}
            <div style={{ marginTop: '24px' }}>
              <h4 style={{ marginBottom: '4px' }}>Automation Scope</h4>
              <p style={{ color: '#8b8b8b', marginBottom: '12px' }}>Choose how far the automation pipeline should execute.</p>
              <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
                <div style={{ marginBottom: '8px' }}>
                  <input
                    id="scope-onboarding"
                    name="scope"
                    type="radio"
                    checked={formData.scope === 'onboarding_only'}
                    onChange={() =>
                      setFormData({ ...formData, scope: 'onboarding_only', deploymentAutomation: false, testAutomation: false })
                    }
                  />
                  <label htmlFor="scope-onboarding">Onboarding Only</label>
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <input
                    id="scope-deployment"
                    name="scope"
                    type="radio"
                    checked={formData.scope === 'onboarding_deployment'}
                    onChange={() => setFormData({ ...formData, scope: 'onboarding_deployment', testAutomation: false })}
                  />
                  <label htmlFor="scope-deployment">Onboarding + Deployment</label>
                </div>
                {!isVerified && (
                  <div>
                    <input
                      id="scope-full"
                      name="scope"
                      type="radio"
                      checked={formData.scope === 'full'}
                      onChange={() => setFormData({ ...formData, scope: 'full' })}
                    />
                    <label htmlFor="scope-full">Full Automation (+ Testing)</label>
                  </div>
                )}
              </fieldset>
            </div>
          </ReactEDSWrapper>

          {/* Step 2: SW Repository Connection */}
          <ReactEDSWrapper component={WizardStep} stepTitle="SW Repository Connection" valid={!!formData.repository}>
            <p style={{ color: '#8b8b8b', marginBottom: '16px' }}>Select the software repository connection for this workflow.</p>

            {isVerified ? (
              <>
                <div className="row" style={{ marginBottom: '24px' }}>
                  <div className="column sm-12">
                    <h5>Internal OCI Registry</h5>
                  </div>
                  <div className="column sm-12 md-6 lg-4">
                    <ReactEDSWrapper
                      component={Card}
                      title="Internal OCI Registry"
                      selected={formData.repository === 'internal-oci'}
                      onChange={(selected: any) => setFormData({ ...formData, repository: selected ? 'internal-oci' : '' })}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        <i className="icon icon-cloud" style={{ marginRight: '8px', fontSize: '18px' }} />
                        <span>Pre-verified production artifacts</span>
                      </div>
                      <p style={{ fontSize: '12px', color: '#6a6a6a' }}>
                        Use the internal OCI registry for verified artifacts that are already registered.
                      </p>
                    </ReactEDSWrapper>
                  </div>
                </div>
                <div className="row">
                  <div className="column sm-12">
                    <h5>External Repositories</h5>
                  </div>
                  {repositories.length === 0 ? (
                    <div className="column sm-12">
                      <p style={{ color: '#6a6a6a' }}>
                        No external repositories configured. <Link to="/sw-repositories/external-repositories">Configure one</Link> if
                        needed.
                      </p>
                    </div>
                  ) : (
                    repositories.map((repo) => (
                      <div key={repo.id} className="column sm-12 md-6 lg-4">
                        <ReactEDSWrapper
                          component={Card}
                          title={repo.name}
                          subtitle={getRepositoryTypeInfo(repo.type as RepositoryType)?.name}
                          selected={formData.repository === repo.id}
                          onChange={(selected: any) => setFormData({ ...formData, repository: selected ? repo.id : '' })}
                        >
                          <span>
                            <i className="icon icon-link" style={{ marginRight: '8px' }} />
                            {repo.url}
                          </span>
                          <br />
                          <span>
                            <i className="icon icon-avatar" style={{ marginRight: '8px' }} />
                            {repo.username}
                          </span>
                          <br />
                          <span>
                            <i
                              className="icon icon-alarm-level4"
                              style={{ fontSize: '8px', color: getStatusColor(repo.status), marginRight: '6px' }}
                            />
                            <span style={{ fontSize: '11px', textTransform: 'capitalize', color: '#6a6a6a' }}>{repo.status}</span>
                          </span>
                        </ReactEDSWrapper>
                      </div>
                    ))
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="row" style={{ marginBottom: '24px' }}>
                  <div className="column sm-12">
                    <h5>Ericsson Software Gateway</h5>
                  </div>
                  <div className="column sm-12 md-6 lg-4">
                    <ReactEDSWrapper
                      component={Card}
                      title="Ericsson Software Gateway"
                      selected={formData.repository === 'swgw'}
                      onChange={(selected: any) => setFormData({ ...formData, repository: selected ? 'swgw' : '' })}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        <i className="icon icon-cloud" style={{ marginRight: '8px', fontSize: '18px' }} />
                        <span>Official Ericsson Software Distribution</span>
                      </div>
                      <p style={{ fontSize: '12px', color: '#6a6a6a' }}>
                        Connect to Ericsson's centralized software gateway for official product releases
                      </p>
                    </ReactEDSWrapper>
                  </div>
                </div>
                <div className="row">
                  <div className="column sm-12">
                    <h5>External Repositories</h5>
                  </div>
                  {repositories.length === 0 ? (
                    <div className="column sm-12">
                      <p style={{ color: '#6a6a6a' }}>
                        No external repositories configured. <Link to="/sw-repositories/external-repositories">Configure one</Link> if
                        needed.
                      </p>
                    </div>
                  ) : (
                    repositories.map((repo) => (
                      <div key={repo.id} className="column sm-12 md-6 lg-4">
                        <ReactEDSWrapper
                          component={Card}
                          title={repo.name}
                          subtitle={getRepositoryTypeInfo(repo.type as RepositoryType)?.name}
                          selected={formData.repository === repo.id}
                          onChange={(selected: any) => setFormData({ ...formData, repository: selected ? repo.id : '' })}
                        >
                          <span>
                            <i className="icon icon-link" style={{ marginRight: '8px' }} />
                            {repo.url}
                          </span>
                          <br />
                          <span>
                            <i className="icon icon-avatar" style={{ marginRight: '8px' }} />
                            {repo.username}
                          </span>
                          <br />
                          <span>
                            <i
                              className="icon icon-alarm-level4"
                              style={{ fontSize: '8px', color: getStatusColor(repo.status), marginRight: '6px' }}
                            />
                            <span style={{ fontSize: '11px', textTransform: 'capitalize', color: '#6a6a6a' }}>{repo.status}</span>
                          </span>
                        </ReactEDSWrapper>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </ReactEDSWrapper>

          {/* Step 3: Target Environment */}
          <ReactEDSWrapper component={WizardStep} stepTitle="Target Environment" valid={!!formData.eiapCluster && formData.enms.length > 0}>
            <h3>
              Select Target Environment <span style={{ color: '#0069C2' }}>*</span>
            </h3>
            <p style={{ color: '#8b8b8b', marginBottom: '16px' }}>
              Select the EIAP Cluster and the linked ENMs where SW onboarding will execute.
            </p>

            <div className="row" style={{ marginBottom: '24px' }}>
              <div className="column sm-12">
                <h4>Select EIAP Cluster</h4>
              </div>
              {eiapClusters.length === 0 ? (
                <div className="column sm-12">
                  <p style={{ color: '#6a6a6a' }}>
                    No EIAP Clusters configured. <Link to="/integrations/eiap-clusters">Configure one</Link> first.
                  </p>
                </div>
              ) : (
                eiapClusters.map((cluster) => (
                  <div key={cluster.id} className="column sm-12 md-6 lg-4">
                    <ReactEDSWrapper
                      component={Card}
                      title={cluster.name}
                      selected={formData.eiapCluster === cluster.id}
                      onChange={(selected: any) => {
                        if (selected) {
                          setFormData({ ...formData, eiapCluster: cluster.id, enms: [] });
                        } else {
                          setFormData({ ...formData, eiapCluster: '', enms: [] });
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        <i className="icon icon-cloud" style={{ marginRight: '8px', fontSize: '18px' }} />
                        <span>Category: {cluster.category}</span>
                      </div>
                    </ReactEDSWrapper>
                  </div>
                ))
              )}
            </div>

            {formData.eiapCluster && (
              <div className="row">
                <div className="column sm-12">
                  <h4>Select ENMs</h4>
                </div>
                {availableEnms.length === 0 ? (
                  <div className="column sm-12">
                    <p style={{ color: '#6a6a6a' }}>
                      No ENMs linked to this cluster. <Link to="/integrations/enms">Link ENMs</Link> first.
                    </p>
                  </div>
                ) : (
                  availableEnms.map((enm) => (
                    <div key={enm.id} className="column sm-12 md-6 lg-4">
                      <ReactEDSWrapper
                        component={Card}
                        title={enm.name}
                        selected={formData.enms.includes(enm.id)}
                        onChange={(selected: any) => {
                          const updatedEnms = selected ? [...formData.enms, enm.id] : formData.enms.filter((e) => e !== enm.id);
                          setFormData({ ...formData, enms: updatedEnms });
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                          <i className="icon icon-antenna" style={{ marginRight: '8px', fontSize: '18px' }} />
                          <span>{enm.url}</span>
                        </div>
                        <p style={{ fontSize: '12px', color: '#6a6a6a' }}>{enm.description}</p>
                      </ReactEDSWrapper>
                    </div>
                  ))
                )}
              </div>
            )}
          </ReactEDSWrapper>

          {/* Step 4: Onboarding */}
          <ReactEDSWrapper component={WizardStep} stepTitle="Onboarding">
            <h3>
              Configure Selective Onboarding <span style={{ color: '#0069C2' }}>*</span>
            </h3>
            <p style={{ color: '#8b8b8b', marginBottom: '16px' }}>
              When enabled, the pipeline pauses at Artifacts Retrieval/Onboarding for a go/no-go decision. When disabled, it runs without
              pausing (automated).
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <label style={{ fontWeight: '500', minWidth: '180px' }}>Enable Selective Onboarding:</label>
              <ReactEDSWrapper
                component={Switch}
                checked={formData.selectiveOnboarding.enabled}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFormData({ ...formData, selectiveOnboarding: { enabled: e.target.checked } })
                }
                enabledLabel="On"
                disabledLabel="Off"
              />
            </div>
            <p style={{ fontSize: '0.875rem', color: '#6a6a6a' }}>
              {formData.selectiveOnboarding.enabled
                ? 'Pipeline will pause at Artifacts stages and wait for operator go/no-go decision.'
                : 'Pipeline will run continuously without pausing.'}
            </p>
          </ReactEDSWrapper>

          {/* Conditional Steps: Deployment + Testing */}
          {[
            showDeploymentStep ? (
              <ReactEDSWrapper key="deployment" component={WizardStep} stepTitle="Deployment">
                <h3>
                  Configure Deployment Trigger <span style={{ color: '#0069C2' }}>*</span>
                </h3>
                <p style={{ color: '#8b8b8b', marginBottom: '16px' }}>
                  Choose whether the deployment should start manually or automatically. Auto-Deploy On automatically triggers the deployment
                  stage.
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <label style={{ fontWeight: '500', minWidth: '150px' }}>Auto-Deploy:</label>
                  <ReactEDSWrapper
                    component={Switch}
                    checked={formData.deploymentAutomation}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFormData({ ...formData, deploymentAutomation: e.target.checked })
                    }
                    enabledLabel="On"
                    disabledLabel="Off"
                  />
                </div>
              </ReactEDSWrapper>
            ) : null,
            showTestingStep ? (
              <ReactEDSWrapper key="testing" component={WizardStep} stepTitle="Testing">
                <h3>
                  Configure Test Trigger <span style={{ color: '#0069C2' }}>*</span>
                </h3>
                <p style={{ color: '#8b8b8b', marginBottom: '16px' }}>
                  Choose whether testing should start manually or automatically. Auto-Test On automatically triggers the test stage.
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <label style={{ fontWeight: '500', minWidth: '150px' }}>Auto-Test:</label>
                  <ReactEDSWrapper
                    component={Switch}
                    checked={formData.testAutomation}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, testAutomation: e.target.checked })}
                    enabledLabel="On"
                    disabledLabel="Off"
                  />
                </div>
              </ReactEDSWrapper>
            ) : null
          ].filter(Boolean)}

          {/* Review & Create */}
          <ReactEDSWrapper component={WizardStep} stepTitle="Review & Create" valid={!!formData.flowName}>
            <h4>Review Your Automation Flow Configuration</h4>
            <div className="row" style={{ marginTop: '16px' }}>
              <div className="column sm-12">
                <ReactEDSWrapper component={Card} title="Configuration Summary">
                  <div style={{ display: 'grid', gap: '12px' }}>
                    <div>
                      <strong>Name:</strong> {formData.flowName || 'Not set'}
                    </div>
                    <div>
                      <strong>Description:</strong> {formData.flowDescription || 'Not set'}
                    </div>
                    <div>
                      <strong>Artifact Readiness:</strong>{' '}
                      {formData.environmentType === 'verified' ? 'Ready for Deployment' : 'Requires Validation'}
                    </div>
                    <div>
                      <strong>Automation Scope:</strong>{' '}
                      {formData.scope === 'full'
                        ? 'Full Automation'
                        : formData.scope === 'onboarding_deployment'
                          ? 'Onboarding + Deployment'
                          : 'Onboarding Only'}
                    </div>
                    <div>
                      <strong>Stage Model:</strong> {getStageModel(formData.scope)}
                    </div>
                    <div>
                      <strong>SW Repository:</strong>{' '}
                      {formData.repository === 'swgw'
                        ? 'Ericsson Software Gateway'
                        : formData.repository === 'internal-oci'
                          ? 'Internal OCI Registry'
                          : repositories.find((r) => r.id === formData.repository)?.name || 'Not selected'}
                    </div>
                    <div>
                      <strong>Product Lines:</strong>{' '}
                      {productLines.length > 0 ? productLines.map((pl) => pl.name).join(', ') : 'None mapped'}
                    </div>
                    <div>
                      <strong>EIAP Cluster:</strong> {eiapClusters.find((c) => c.id === formData.eiapCluster)?.name || 'Not selected'}
                    </div>
                    <div>
                      <strong>ENMs:</strong>{' '}
                      {formData.enms.length > 0
                        ? formData.enms
                            .map((enmId) => enms.find((e) => e.id === enmId)?.name)
                            .filter(Boolean)
                            .join(', ')
                        : 'None selected'}
                    </div>
                    <div>
                      <strong>Selective Onboarding:</strong> {formData.selectiveOnboarding.enabled ? 'Enabled' : 'Disabled'}
                    </div>
                    {showDeploymentStep && (
                      <div>
                        <strong>Auto-Deploy:</strong> {formData.deploymentAutomation ? 'On' : 'Off'}
                      </div>
                    )}
                    {showTestingStep && (
                      <div>
                        <strong>Auto-Test:</strong> {formData.testAutomation ? 'On' : 'Off'}
                      </div>
                    )}
                  </div>
                </ReactEDSWrapper>
              </div>
            </div>
          </ReactEDSWrapper>
        </ReactEDSWrapper>
      </ReactEDSWrapper>
    </div>
  );
}
